import { useCallback, useEffect, useState } from 'react';
import { Counter } from '../../components/Counter';
import { DateRangePicker } from '../../components/DateRangePicker';
import { GuestPicker } from '../../components/GuestPicker';
import {
  CounterLabel,
  CounterRow,
  Dropdown as GuestDropdown,
} from '../../components/GuestPicker/GuestPicker.styles';
import { ChevronDownIcon } from '../../icons/ChevronDownIcon';
import { ChevronLeftIcon } from '../../icons/ChevronLeftIcon';
import { CloseIcon } from '../../icons/CloseIcon';
import { CategoryOption, ChevronIcon } from './JourneyPicker.styles';
import {
  GuestModalBody,
  MobileBackButton,
  MobileCalendarWrapper,
  MobileCategoryDropdownButton,
  MobileCategoryDropdownList,
  MobileCategoryWrapper,
  MobileEditButton,
  MobileModalBody,
  MobileModalCloseButton,
  MobileModalFooter,
  MobileModalHeader,
  MobileModalOverlay,
  MobileModalTitle,
  MobileSearchButton,
  MobileSection,
  MobileSectionValue,
  MobileSubModalHeader,
  MobileTrigger,
  MobileTriggerSubtitle,
  MobileTriggerTitle,
} from './JourneyPickerMobile.styles';
import type { JourneyPickerSharedProps } from './JourneyPickerView.types';

export interface JourneyPickerMobileProps extends JourneyPickerSharedProps {
  triggerTitle: string;
  triggerSubtitle: string;
  closeLabel: string;
  backLabel: string;
  editLabel: string;
  headingLabel: string;
  updateLabel: string;
  changeDateLabel: string;
  changeGuestsLabel: string;
  guestsAriaLabel: string;
}

export function JourneyPickerMobile({
  triggerTitle,
  triggerSubtitle,
  showCategory,
  categories,
  category,
  currentCategoryLabel,
  onCategorySelect,
  fromDate,
  toDate,
  isSingleDate,
  datesLabel,
  onRangeSelect,
  displayGuests,
  guestMode,
  adults,
  children,
  guestsLabel,
  onAdultsChange,
  onChildrenChange,
  availability,
  isLoadingAvailability,
  onMonthChange,
  onSearch,
  searchLabel,
  closeLabel,
  backLabel,
  editLabel,
  headingLabel,
  updateLabel,
  changeDateLabel,
  changeGuestsLabel,
  guestsAriaLabel,
  datePickerLabels,
  guestPickerLabels,
}: JourneyPickerMobileProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [guestModalOpen, setGuestModalOpen] = useState(false);
  const [categoryListOpen, setCategoryListOpen] = useState(false);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setCalendarOpen(false);
    setGuestModalOpen(false);
    setCategoryListOpen(false);
  }, []);

  const openModal = useCallback(() => {
    setCalendarOpen(false);
    setGuestModalOpen(false);
    setCategoryListOpen(false);
    setModalOpen(true);
  }, []);

  const handleRangeSelect = useCallback(
    (checkIn: string, checkOut: string) => {
      onRangeSelect(checkIn, checkOut);
      if (isSingleDate || checkOut) setCalendarOpen(false);
    },
    [onRangeSelect, isSingleDate]
  );

  const handleCategoryClick = useCallback(
    (id: string) => {
      closeModal();
      onCategorySelect(id);
    },
    [closeModal, onCategorySelect]
  );

  const handleSearchClick = useCallback(() => {
    closeModal();
    onSearch();
  }, [closeModal, onSearch]);

  useEffect(() => {
    if (!modalOpen) return;
    if (typeof document === 'undefined') return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [modalOpen]);

  return (
    <>
      <MobileTrigger type="button" onClick={openModal}>
        <MobileTriggerTitle>{triggerTitle}</MobileTriggerTitle>
        <MobileTriggerSubtitle>{triggerSubtitle}</MobileTriggerSubtitle>
      </MobileTrigger>

      {modalOpen && (
        <MobileModalOverlay role="dialog" aria-modal="true">
          {calendarOpen ? (
            <>
              <MobileSubModalHeader>
                <MobileBackButton
                  type="button"
                  onClick={() => setCalendarOpen(false)}
                  aria-label={backLabel}
                >
                  <ChevronLeftIcon />
                </MobileBackButton>
                <MobileModalTitle>{changeDateLabel}</MobileModalTitle>
                <span />
              </MobileSubModalHeader>
              <MobileCalendarWrapper>
                <DateRangePicker
                  checkIn={fromDate || null}
                  checkOut={isSingleDate ? null : toDate || null}
                  singleDateMode={isSingleDate}
                  availability={availability}
                  isLoading={isLoadingAvailability}
                  onRangeSelect={handleRangeSelect}
                  onMonthChange={onMonthChange}
                  columnView
                  numberOfMonths={1}
                  renderInline
                  labels={datePickerLabels}
                />
              </MobileCalendarWrapper>
            </>
          ) : (
            <>
              <MobileModalHeader>
                <MobileModalCloseButton
                  type="button"
                  onClick={closeModal}
                  aria-label={closeLabel}
                >
                  <CloseIcon size={24} />
                </MobileModalCloseButton>
              </MobileModalHeader>
              <MobileModalBody>
                <MobileModalTitle>{headingLabel}</MobileModalTitle>
                {showCategory && (
                  <MobileCategoryWrapper>
                    <MobileCategoryDropdownButton
                      type="button"
                      onClick={() => setCategoryListOpen((open) => !open)}
                      aria-expanded={categoryListOpen}
                    >
                      {currentCategoryLabel}
                      <ChevronIcon>
                        <ChevronDownIcon />
                      </ChevronIcon>
                    </MobileCategoryDropdownButton>
                    {categoryListOpen && (
                      <MobileCategoryDropdownList>
                        {categories!.map((cat) => (
                          <CategoryOption
                            key={cat.id}
                            type="button"
                            $active={cat.id === category}
                            onClick={() => handleCategoryClick(cat.id)}
                          >
                            {cat.label}
                          </CategoryOption>
                        ))}
                      </MobileCategoryDropdownList>
                    )}
                  </MobileCategoryWrapper>
                )}

                <MobileSection>
                  <MobileSectionValue>{datesLabel}</MobileSectionValue>
                  <MobileEditButton type="button" onClick={() => setCalendarOpen(true)}>
                    {editLabel}
                  </MobileEditButton>
                </MobileSection>

                {displayGuests && (
                  <MobileSection>
                    <MobileSectionValue>{guestsLabel}</MobileSectionValue>
                    <MobileEditButton
                      type="button"
                      onClick={() => setGuestModalOpen(true)}
                    >
                      {editLabel}
                    </MobileEditButton>
                  </MobileSection>
                )}
              </MobileModalBody>
              <MobileModalFooter>
                <MobileSearchButton type="button" onClick={handleSearchClick}>
                  {searchLabel}
                </MobileSearchButton>
              </MobileModalFooter>
            </>
          )}
        </MobileModalOverlay>
      )}

      {guestModalOpen && (
        <MobileModalOverlay role="dialog" aria-modal="true" aria-label={guestsAriaLabel}>
          <MobileSubModalHeader>
            <MobileBackButton
              type="button"
              onClick={() => setGuestModalOpen(false)}
              aria-label={backLabel}
            >
              <ChevronLeftIcon />
            </MobileBackButton>
            <MobileModalTitle>{changeGuestsLabel}</MobileModalTitle>
            <span />
          </MobileSubModalHeader>
          <GuestModalBody>
            {guestMode === 'combined' ? (
              <GuestDropdown $inline>
                <CounterRow>
                  <CounterLabel>{guestPickerLabels?.guests ?? 'Guests'}</CounterLabel>
                  <Counter
                    value={adults}
                    min={1}
                    max={20}
                    onChange={onAdultsChange}
                    decreaseLabel={guestPickerLabels?.decreaseAdults}
                    increaseLabel={guestPickerLabels?.increaseAdults}
                  />
                </CounterRow>
              </GuestDropdown>
            ) : (
              <GuestPicker
                adults={adults}
                childCount={children}
                onAdultsChange={onAdultsChange}
                onChildrenChange={onChildrenChange}
                renderInline
                labels={guestPickerLabels}
              />
            )}
          </GuestModalBody>
          <MobileModalFooter>
            <MobileSearchButton type="button" onClick={() => setGuestModalOpen(false)}>
              {updateLabel}
            </MobileSearchButton>
          </MobileModalFooter>
        </MobileModalOverlay>
      )}
    </>
  );
}
